import React, { useState } from 'react';
import { supabase, getAuthRedirectUrl } from '../lib/supabase';
import { Mail, Lock, LogIn, UserPlus, X, Eye, EyeOff, KeyRound } from 'lucide-react';

const Auth = ({ onAuthSuccess, onClose, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isLogin) {
        // Sign in
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          setMessage('Successfully signed in!');
          setTimeout(() => {
            onAuthSuccess(data.user);
          }, 500);
        }
      } else {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          setMessage('Account created! Check your email to confirm your account.');
          // Auto switch to login after successful signup
          setTimeout(() => {
            setIsLogin(true);
            setMessage('You can now sign in!');
          }, 2000);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordRecovery = async () => {
    setError(null);
    setMessage(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email above first.');
      return;
    }
    setRecoveryLoading(true);
    try {
      const redirectTo = getAuthRedirectUrl();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo,
      });
      if (resetError) throw resetError;
      setMessage('Check your email for a link to reset your password.');
    } catch (err) {
      setError(err.message || 'Could not send reset email.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="auth-modal-root fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="glass-card rounded-2xl border border-[#3B82F6]/30 p-8 w-full max-w-md relative">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-[#64748B] hover:text-[#1E293B] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="mb-6">
          <h2 className="serif text-3xl font-bold text-[#1E293B] mb-2">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="text-sm text-[#475569]">
            {isLogin 
              ? 'Sign in to continue your Bible study journey' 
              : 'Join us to start your spiritual journey'}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl text-green-600 text-sm">
              {message}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#475569] mb-2">
              Email
            </label>
            <div className="relative isolate">
              <Mail
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#64748B]"
                aria-hidden
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
                className="relative z-0 w-full rounded-xl border border-[#3B82F6]/30 bg-[#E2E8F0]/30 py-3 pl-10 pr-4 text-[#1E293B] placeholder:text-[#64748B] glass-card focus:border-[#e2c275]/50 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#475569] mb-2">
              Password
            </label>
            <div className="relative isolate">
              <Lock
                className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#64748B]"
                aria-hidden
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                className="relative z-0 w-full rounded-xl border border-[#3B82F6]/30 bg-[#E2E8F0]/30 py-3 pl-10 pr-12 text-[#1E293B] placeholder:text-[#64748B] glass-card focus:border-[#e2c275]/50 focus:outline-none"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPassword((v) => !v);
                }}
                className="auth-password-toggle absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#64748B] hover:bg-[#E2E8F0]/60 hover:text-[#1E293B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/50"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center" aria-hidden>
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || recoveryLoading}
            className="w-full py-3 bg-[#e2c275] text-black font-semibold rounded-xl hover:bg-[#e2c275]/90 transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : isLogin ? (
              <>
                <LogIn className="w-4 h-4 shrink-0" aria-hidden />
                Sign In
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 shrink-0" aria-hidden />
                Sign Up
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setMessage(null);
            }}
            className="auth-text-link inline-flex flex-wrap items-center justify-center gap-1.5 text-sm text-[#475569] hover:text-[#1E293B] transition-colors shadow-none hover:shadow-none focus:outline-none focus-visible:ring-0"
          >
            {isLogin ? (
              <UserPlus className="w-3.5 h-3.5 shrink-0 text-[#3B82F6]" aria-hidden />
            ) : (
              <LogIn className="w-3.5 h-3.5 shrink-0 text-[#3B82F6]" aria-hidden />
            )}
            <span>
              {isLogin
                ? "Don't have an account? "
                : 'Already have an account? '}
              <span className="font-semibold text-[#3B82F6] hover:text-[#e2c275]">
                {isLogin ? 'Sign up' : 'Sign in'}
              </span>
            </span>
          </button>
          {isLogin && (
            <button
              type="button"
              onClick={handlePasswordRecovery}
              disabled={loading || recoveryLoading}
              className="auth-text-link inline-flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#3B82F6] hover:text-[#2563EB] transition-colors shadow-none hover:shadow-none focus:outline-none focus-visible:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <KeyRound className="w-3.5 h-3.5 shrink-0" aria-hidden />
              {recoveryLoading ? 'Sending…' : 'Recover password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;

